import { Autocomplete, ListItemText, TextField } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { FC, MouseEvent, useRef, useState, useEffect } from "react";
import { Cookies } from "utils";
import {
  CI_EMAILID_COOKIE,
  EMAILID_COOKIE,
  FIRST_NAME_COOKIE,
  MOCK_USER_COOKIE,
  CI_FIRST_NAME_COOKIE,
} from "constant";
import MButton from "components/momentum/MButton";
import useSWR from "swr";
import { Person } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  iconContainer: {
    marginLeft: theme.spacing(1),
    cursor: "pointer",
    "&:hover": {
      color: "#00a0d1",
    },
  },
  logoutIcon: {
    verticalAlign: "middle",
  },
}));

interface TestUser {
  userId: string;
  displayName: string;
}

interface TestUserObject {
  debug: boolean;
  users: TestUser[];
}

const MockUserPopper: FC = () => {
  const classes = useStyles();
  const [message, setMessage] = useState("");
  const { data } = useSWR<TestUserObject>("/api/dataservice/api/auth/v2/test-users");

  const mockerEmail = Cookies.get(MOCK_USER_COOKIE) ?? "";
  const [inputValue, setInputValue] = useState(mockerEmail);
  const [openDiv, setOpenDiv] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    setOpenDiv(!openDiv);
  };

  const handleValue = (value: string) => {
    const regex = /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
    if (!value) {
      setMessage("");
      return;
    }

    if (value && regex.test(value)) {
      setMessage("");
      const [username] = value.split("@");
      Cookies.set(MOCK_USER_COOKIE, `"${value}"`);
      Cookies.set(FIRST_NAME_COOKIE, `"${username}"`);
      Cookies.set(EMAILID_COOKIE, `"${value}"`);
      window.location.reload();
    } else {
      setMessage("Please enter valid email");
    }
  };

  const onRevert = () => {
    Cookies.remove(MOCK_USER_COOKIE);
    const userEmail = Cookies.get(CI_EMAILID_COOKIE);
    const userName = Cookies.get(CI_FIRST_NAME_COOKIE);
    if (userEmail && userName) {
      Cookies.set(EMAILID_COOKIE, `"${userEmail}"`);
      Cookies.set(FIRST_NAME_COOKIE, `"${userName}"`);
      window.location.reload();
    } else {
      window.location.assign("/spng");
    }
  };

  useEffect(() => {
    const handleClickOutside: EventListener = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpenDiv(false); // when click outside this div
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const options = data?.users || [];

  return (
    <div
      ref={containerRef}
      style={{ position: "relative" }}
    >
      <div
        className={classes.iconContainer}
        onClick={onClick}
        id="pericon"
      >
        <i className={`cui-icon icon icon-assign-privilege_20 ${classes.logoutIcon}`} />
      </div>
      <div
        style={{
          position: "absolute",
          left: "-200px",
          top: "30px",
          zIndex: 20,
          width: "300px",
          padding: "10px 20px 30px 20px",
          borderRadius: "5px",
          border: "1px solid #b2b2b2",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          backgroundColor: "#FFF",
          display: openDiv ? "block" : "none",
        }}
      >
        <MButton
          onClick={onRevert}
          buttonType="blue"
          sx={{ float: "right" }}
        >
          Revert
        </MButton>

        <Autocomplete
          sx={{
            marginTop: "10px",
            "& .MuiAutocomplete-popper li": {
              width: "100%",
            },
          }}
          slotProps={{
            popper: {
              sx: { width: "300px !important", border: "1px solid #b2b2b2" },
            },
          }}
          freeSolo
          fullWidth
          options={options}
          value={inputValue}
          getOptionLabel={(option) => (typeof option === "string" ? option : option.userId)}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li
                {...rest}
                key={option.userId}
              >
                <Person sx={{ color: "GrayText" }} />
                <ListItemText
                  sx={{ marginLeft: "5px" }}
                  primary={option.displayName}
                  secondary={option.userId}
                />
              </li>
            );
          }}
          onChange={(_, newValue) => {
            const value =
              typeof newValue === "string" ? newValue : (newValue as TestUser)?.userId || "";
            setInputValue(value);
            handleValue(value);
          }}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Enter Mock User Email"
              variant="standard"
              size="small"
              error={!!message}
              helperText={message}
              onKeyDown={(e) => {
                if (e.key === "Enter" && data?.debug) {
                  // ignore in PROD
                  handleValue(inputValue);
                }
              }}
            />
          )}
        />
      </div>
    </div>
  );
};

export default MockUserPopper;
